import type { FC, PropsWithChildren } from 'react';
import React, { createContext, useMemo } from 'react';
import type { AudiHeaderConfig } from '../interfaces/header-components.interfaces';
import { getMiniCartConfiguration, type MiniCartConfiguration } from './getMiniCartConfiguration';
import type { NormalizeContentConfig } from '../utils/normalize-falcon-data';

export interface ConfigContextState extends MiniCartConfiguration, NormalizeContentConfig {
  readonly featureAppId: string;
  readonly loginFeatureAppUrl: string;
  readonly searchInputFeatureAppUrl: string;
  readonly searchResultsFeatureAppUrl: string;
  readonly renderDealerContext: boolean;
  readonly tierOneUrl: string;
  readonly dealerSearchFeatureAppUrl: string;
  readonly dealerSearchConfigPath: string;
  readonly dealerSearch: boolean;
  readonly dealerSearchGeolocationEnabled: boolean;
  readonly geoLocationOption: 'optIn' | 'optOut';
  readonly dealerSearchBenefitEnabled: boolean;
  readonly useOneLayer: boolean;
  readonly useUserMenu: boolean;
  readonly disableMyAudiMenu: boolean;
  readonly useAiSearch: boolean;
  readonly aiSearchFeatureAppUrl: string;
  readonly aiSearchFootnoteUrls: string[];
  readonly aiSearchPriceFootnote: string;
}

export const ConfigContext = createContext<ConfigContextState>(
  undefined as unknown as ConfigContextState,
);

export interface ConfigContextProviderProps extends PropsWithChildren {
  readonly featureAppId: string;
  readonly config?: AudiHeaderConfig;
}

export const ConfigContextProvider: FC<ConfigContextProviderProps> = ({
  config,
  featureAppId,
  children,
}) => {
  const state = useMemo<ConfigContextState>(() => {
    const {
      usermenuFeatureAppVersion = '9',
      searchFeatureAppVersion = '8.2.1',
      searchResultsFeatureAppVersion = '8.2.4',
      dealerSearchFeatureAppVersion = 'v4.8.6',
      // dealerSearchConfigPath value must be overridden from live config values, this is placeholder
      dealerSearchConfigPath = '/content/dam/teams/popeye/feature-apps/other-f-as/fa-dealer-search-latest',
      renderDealerContext = false,
      dealerSearch = false,
      dealerSearchGeolocationEnabled = false,
      geoLocationOption: geoLocationOption = 'optOut', // optOut as default to Auto connect dealer, dealerSearchGeolocationEnabled must be true
      dealerSearchBenefitEnabled = false,
      useOneLayer = false,
      useUserMenu = false,
      disableMyAudiMenu = false,
      OneShopEnabled,
      tierOneUrl = '',
      useAiSearch = false,
      aiSearchFeatureAppVersion = 'latest',
      aiSearchFootnoteUrls = [],
      aiSearchPriceFootnote = '',
    } = config ?? {};

    return {
      featureAppId,
      loginFeatureAppUrl: `https://featureapps.audi.com/audi-feature-app-user-menu/${usermenuFeatureAppVersion}/app.js`,
      searchInputFeatureAppUrl: `https://fa-search-input.cdn.prod.arcade.apps.one.audi/${searchFeatureAppVersion}/fh/${process.env.IS_LEGACY ? 'app' : 'app-alpha'}.js`,
      searchResultsFeatureAppUrl: `https://fa-search-results.cdn.prod.arcade.apps.one.audi/${searchResultsFeatureAppVersion}/fh/${process.env.IS_LEGACY ? 'app' : 'app-alpha'}.js`,
      dealerSearchFeatureAppUrl: `https://fa-dealer-search.cdn.prod.arcade.apps.one.audi/${dealerSearchFeatureAppVersion}/fh/${process.env.IS_LEGACY ? 'app' : 'app-alpha'}.js`,
      dealerSearchConfigPath,
      renderDealerContext,
      dealerSearch,
      dealerSearchGeolocationEnabled,
      geoLocationOption,
      dealerSearchBenefitEnabled,
      useOneLayer,
      useUserMenu,
      disableMyAudiMenu,
      OneShopEnabled,
      tierOneUrl,
      useAiSearch,
      aiSearchFeatureAppUrl: `https://fa-genius.cdn.prod.arcade.apps.one.audi/${aiSearchFeatureAppVersion}/fh/app-alpha.js`,
      aiSearchFootnoteUrls,
      aiSearchPriceFootnote,
      ...getMiniCartConfiguration(config),
    };
  }, [featureAppId, config]);

  return <ConfigContext.Provider value={state}>{children}</ConfigContext.Provider>;
};
